import Button from "../../../shared/components/Button";
import Icons from "../../../shared/constants/icon.constant";
import SolidIconBuilder from "../../../shared/helpers/SolidIconBuilder";
import type { IMembershipPlan } from "../interfaces/membership.interface";

interface TierCardProps {
  data: IMembershipPlan;
  buttonText: string;
  onSelected(): void;
  loading?: boolean;
  disabled?: boolean;
}

export default function TierCard({
  data,
  buttonText,
  onSelected,
  loading = false,
  disabled = false,
}: Readonly<TierCardProps>) {
  return (
    <div className="flex flex-col rounded-2xl border border-border p-4 w-[290px] min-h-[50vh]">
      <div className="flex gap-2 items-start">
        <SolidIconBuilder
          icon={Icons.BrandLogo({ height: 35, width: 35 })}
          color={data.logoColor ?? "#6e8968"}
        />
        <div className="flex flex-col flex-1 gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-md font-semibold">{data.name}</div>
            {data.planCode && (
              <span className="rounded-full bg-white/10 px-2 py-[2px] text-[10px] uppercase tracking-wide text-white/80 border border-white/10">
                {data.planCode}
              </span>
            )}
            {typeof data.level === "number" && (
              <span className="rounded-full bg-white/5 px-2 py-[2px] text-[10px] text-white/70 border border-white/5">
                Level {data.level}
              </span>
            )}
            {data.isPopular && (
              <span className="rounded-full bg-amber-500/20 px-2 py-[2px] text-[10px] text-amber-200 border border-amber-400/40">
                Most Popular
              </span>
            )}
          </div>
          <div className="text-xs text-label-dark leading-snug">
            {data.description}
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className="text-md font-semibold">{data.price}</div>
          <div className="text-xs text-label-dark">{data.billingPeriod}</div>
        </div>
      </div>
      <div className="flex flex-col flex-1">
        {data.benefits.length > 0 ? (
          data.benefits.map((benefit) => (
            <div key={benefit.title} className="mt-5">
              <div className="text-label-dark text-xs">{benefit.title}:</div>
              {benefit.items.map((item) => (
                <div className="flex gap-2 items-start" key={item.label}>
                  <div className="mt-1.5">
                    <SolidIconBuilder
                      icon={(item.enabled ? Icons.Correct : Icons.Wrong)({
                        width: 10,
                        height: 10,
                      })}
                      color={item.enabled ? "#09B286" : "#F06A67"}
                    />
                  </div>
                  <div className="font-semibold text-sm text-wrap break-words w-full pr-2">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="mt-5 text-sm text-label-dark">
            Benefits for this plan will appear here.
          </div>
        )}
      </div>
      <div className="w-full mt-4 flex justify-center">
        <Button
          fluid
          className="w-xs"
          onClick={onSelected}
          loading={loading}
          disabled={disabled || loading}>
          {buttonText}
        </Button>
      </div>
    </div>
  );
}
